import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Ruler, Save } from 'lucide-react'
import { useReducer, useState, type ReactNode } from 'react'

import { LabelLayoutSchema } from '@aeris/contracts'
import { renderLayoutCalibrationHtml } from '@aeris/printing'
import {
  Button,
  ConfirmDialog,
  Input,
  Spinner,
  useScopedKeydown,
  Workspace,
} from '@aeris/ui'

import { FieldList } from '../components/label-editor/field-list.tsx'
import {
  ElementInspector,
  SheetInspector,
} from '../components/label-editor/inspectors.tsx'
import { PreviewCanvas } from '../components/label-editor/preview-canvas.tsx'
import { RollTestDialog } from '../components/roll-test-dialog.tsx'
import {
  createLabelTemplate,
  fetchLabelTemplates,
  updateLabelTemplate,
} from '../lib/api/labels.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { printHtmlDocument } from '../lib/label-print.ts'
import {
  draftFromLayout,
  draftReducer,
  emptyDraft,
  formatDecimalInput,
  layoutFromDraft,
  newBarcodeElement,
  newTextElement,
  type LabelDraft,
} from '../lib/label-template-draft.ts'

/**
 * O editor de modelo de etiqueta — F129.
 *
 * A primeira versão copiou o Gerador de Etiquetas do GDOOR ao pé da letra: uma
 * tabela de dezessete colunas. O dono abriu um modelo de verdade e disse que
 * estava *"tudo ruim demais"* — e estava: rolando a tabela até a fonte, a
 * coluna que dizia qual campo era aquele sumia.
 *
 * Esta é a de uma ferramenta de desenho, num ESPAÇO DE TRABALHO de tela cheia:
 *
 * - à esquerda, a lista de campos, cada um dizendo o que é;
 * - no centro, a prévia grande — o mesmo desenho que imprime —, onde clicar
 *   num campo o seleciona;
 * - à direita, as propriedades SÓ do campo selecionado; sem seleção, o papel e
 *   a etiqueta.
 *
 * Setas do teclado movem o campo (0,5 mm; Shift, 5 mm), no lugar do arrastar
 * com o mouse, que continua fora.
 */

const isTyping = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null
  if (element === null || typeof element.closest !== 'function') return false
  return (
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) ||
    element.isContentEditable ||
    // Menu aberto ou diálogo: as setas são deles.
    element.closest('[role="menu"],[role="dialog"]') !== null
  )
}

export const ModeloDeEtiquetaEditarPage = (): ReactNode => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canWrite = allows('catalog.write')

  // `strict: false`: o mesmo componente abre `novo` e um id de verdade.
  const params = useParams({ strict: false })
  const id = params.id ?? 'novo'
  const isNew = id === 'novo'

  const templates = useQuery({
    queryKey: ['label-templates'],
    queryFn: async ({ signal }) => await fetchLabelTemplates('active', signal),
    enabled: !isNew,
  })
  const existing = isNew
    ? undefined
    : (templates.data ?? []).find((template) => template.id === id)

  const [draft, dispatch] = useReducer(draftReducer, undefined, emptyDraft)
  const [baseline, setBaseline] = useState<LabelDraft>(emptyDraft)
  const [seededFor, setSeededFor] = useState<string | null>(
    isNew ? 'novo' : null
  )
  const [selected, setSelected] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [leaving, setLeaving] = useState(false)
  const [rollTest, setRollTest] = useState(false)

  /*
   * Semear o rascunho uma vez, quando o modelo chega. Depois disso o que está
   * na tela é da pessoa: um refetch em segundo plano não pode desfazer o que
   * ela acabou de digitar.
   */
  if (existing !== undefined && seededFor !== existing.id) {
    const seeded = draftFromLayout(existing.name, existing.layout)
    setSeededFor(existing.id)
    setBaseline(seeded)
    dispatch({ type: 'reset', draft: seeded })
  }

  const layout = layoutFromDraft(draft)
  const validation = LabelLayoutSchema.safeParse(layout)
  const nameMissing = draft.name.trim() === ''
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline)
  const current = selected === null ? undefined : draft.elements[selected]

  const save = useMutation({
    mutationFn: async () => {
      const name = draft.name.trim()
      return existing === undefined
        ? await createLabelTemplate({ name, layout })
        : await updateLabelTemplate(existing.id, {
            version: existing.version,
            name,
            layout,
          })
    },
    onSuccess: async (saved) => {
      setError(null)
      const now = draftFromLayout(saved.name, saved.layout)
      setBaseline(now)
      dispatch({ type: 'reset', draft: now })
      setSeededFor(saved.id)
      await queryClient.invalidateQueries({ queryKey: ['label-templates'] })
      if (isNew) {
        await navigate({
          to: '/modelo-de-etiqueta/$id',
          params: { id: saved.id },
          replace: true,
        })
      }
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar o modelo.'
      )
    },
  })

  const back = async (): Promise<void> => {
    await navigate({ to: '/modelo-de-etiqueta' })
  }

  const remove = (index: number): void => {
    setSelected(null)
    dispatch({ type: 'remove', index })
  }

  const onKey = (event: KeyboardEvent): void => {
    if (selected === null || current === undefined || isTyping(event.target)) {
      return
    }
    const step = event.shiftKey ? 5 : 0.5
    const moves: Partial<Record<string, readonly [number, number]>> = {
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
    }
    const move = moves[event.key]
    if (move !== undefined) {
      event.preventDefault()
      dispatch({
        type: 'nudge',
        index: selected,
        dxMm: move[0],
        dyMm: move[1],
      })
      return
    }
    if (event.key === 'Delete') {
      event.preventDefault()
      remove(selected)
      return
    }
    if (event.key === 'Escape') setSelected(null)
  }

  // O tratador lê a seleção e o rascunho DESTA renderização, e
  // `useScopedKeydown` chama sempre o mais recente — e, no Modo PC, só quando a
  // tecla foi apertada nesta janela (F138).
  useScopedKeydown(onKey)

  if (!isNew && templates.isPending) {
    return (
      <Workspace className="items-center justify-center">
        <Spinner label="Carregando modelo" />
      </Workspace>
    )
  }

  if (!isNew && existing === undefined) {
    return (
      <Workspace className="items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-(length:--text-lg) font-(--weight-semibold)">
          Modelo não encontrado
        </h1>
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          {templates.isError
            ? 'Não foi possível carregar os modelos. Verifique a conexão.'
            : 'Ele pode ter sido apagado por outra pessoa.'}
        </p>
        <Button variant="secondary" asChild>
          <Link to="/modelo-de-etiqueta">Voltar para os modelos</Link>
        </Button>
      </Workspace>
    )
  }

  const measures = draft.measures
  const summary = [
    measures.paper === 'roll'
      ? `Bobina de ${formatDecimalInput(measures.pageWidthMm)} mm`
      : `Folha ${formatDecimalInput(measures.pageWidthMm)} × ${formatDecimalInput(measures.pageHeightMm)} mm`,
    `${String(layout.columns)} por linha`,
    `etiqueta ${formatDecimalInput(measures.labelWidthMm)} × ${formatDecimalInput(measures.labelHeightMm)} mm`,
  ].join(' · ')

  const firstIssue = validation.success ? null : validation.error.issues[0]
  const notices = [
    canWrite ? null : 'Só quem administra o catálogo pode salvar modelos.',
    error,
    firstIssue === null || firstIssue === undefined
      ? null
      : `Há um valor fora do permitido (${firstIssue.path.join(' › ')}): ${firstIssue.message}`,
  ].filter((notice): notice is string => notice !== null)

  return (
    <Workspace>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-(--border-default) bg-(--surface) px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />}
          onClick={() => {
            if (dirty) setLeaving(true)
            else void back()
          }}
        >
          Modelos
        </Button>
        <span aria-hidden="true" className="h-6 w-px bg-(--border-default)" />
        <Input
          aria-label="Nome do modelo"
          placeholder="Nome do modelo"
          value={draft.name}
          invalid={nameMissing && dirty}
          className="h-9 w-72 max-w-full font-(--weight-semibold)"
          onChange={(event) => {
            dispatch({ type: 'rename', name: event.target.value })
          }}
        />
        <span className="text-(length:--text-xs) text-(--text-muted)">
          {summary}
        </span>
        <div className="ml-auto flex items-center gap-3">
          <span
            aria-live="polite"
            className="text-(length:--text-xs) text-(--text-secondary)"
          >
            {save.isPending
              ? 'Salvando…'
              : nameMissing && dirty
                ? 'Dê um nome ao modelo para salvar'
                : dirty
                  ? 'Alterações não salvas'
                  : existing === undefined
                    ? ''
                    : 'Tudo salvo'}
          </span>
          {measures.paper === 'sheet' ? (
            /*
             * F140: as medidas que estão na tela, salvas ou não — é assim que
             * se acerta o vão entre fileiras: mudar, imprimir, pôr contra a luz.
             */
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Ruler aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                printHtmlDocument(renderLayoutCalibrationHtml(layout)).catch(
                  () => {
                    setError('Não foi possível abrir a impressão.')
                  }
                )
              }}
            >
              Folha de teste
            </Button>
          ) : (
            /*
             * F264: na bobina o teste sai no próprio adesivo, e antes dele vem
             * o papel do driver — por isso abre o diálogo com os passos, com
             * as medidas que estão na tela, salvas ou não.
             */
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Ruler aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setRollTest(true)
              }}
            >
              Teste da bobina
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            loading={save.isPending}
            disabled={!canWrite || nameMissing || !validation.success || !dirty}
            leadingIcon={<Save aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              save.mutate()
            }}
          >
            Salvar
          </Button>
        </div>
      </header>

      {notices.length === 0 ? null : (
        <div
          role="alert"
          className="flex flex-col gap-0.5 border-b border-(--border-default) bg-(--surface-sunken) px-4 py-2 text-(length:--text-sm) text-(--danger)"
        >
          {notices.map((notice) => (
            <p key={notice}>{notice}</p>
          ))}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-auto xl:flex-row xl:overflow-hidden">
        <aside className="flex max-h-80 shrink-0 flex-col border-b border-(--border-default) bg-(--surface) xl:max-h-none xl:w-72 xl:border-r xl:border-b-0">
          <FieldList
            elements={draft.elements}
            selected={selected}
            onSelect={setSelected}
            onAddText={(source) => {
              setSelected(draft.elements.length)
              dispatch({ type: 'add', element: newTextElement(source) })
            }}
            onAddBarcode={() => {
              setSelected(draft.elements.length)
              dispatch({ type: 'add', element: newBarcodeElement() })
            }}
            onDuplicate={(index) => {
              setSelected(index + 1)
              dispatch({ type: 'duplicate', index })
            }}
            onRemove={remove}
          />
        </aside>

        <div className="order-first flex min-h-[24rem] min-w-0 flex-1 flex-col xl:order-none">
          <PreviewCanvas
            layout={layout}
            selected={current === undefined ? null : selected}
            onSelect={setSelected}
            onKey={onKey}
          />
        </div>

        <aside
          aria-label="Propriedades"
          className="shrink-0 border-t border-(--border-default) bg-(--surface) xl:w-80 xl:overflow-auto xl:border-t-0 xl:border-l"
        >
          {current === undefined || selected === null ? (
            <SheetInspector
              measures={draft.measures}
              onChange={(patch) => {
                dispatch({ type: 'measures', patch })
              }}
            />
          ) : (
            <ElementInspector
              key={selected}
              element={current}
              onText={(patch) => {
                dispatch({ type: 'text', index: selected, patch })
              }}
              onBarcode={(patch) => {
                dispatch({ type: 'barcode', index: selected, patch })
              }}
              onNudge={(dxMm, dyMm) => {
                dispatch({ type: 'nudge', index: selected, dxMm, dyMm })
              }}
              onDuplicate={() => {
                setSelected(selected + 1)
                dispatch({ type: 'duplicate', index: selected })
              }}
              onRemove={() => {
                remove(selected)
              }}
            />
          )}
        </aside>
      </div>

      {rollTest ? (
        <RollTestDialog
          layout={layout}
          onClose={() => {
            setRollTest(false)
          }}
        />
      ) : null}

      <ConfirmDialog
        open={leaving}
        onOpenChange={setLeaving}
        title="Sair sem salvar?"
        description="As alterações feitas neste modelo serão perdidas."
        confirmLabel="Sair sem salvar"
        tone="danger"
        onConfirm={async () => {
          setLeaving(false)
          await back()
        }}
      />
    </Workspace>
  )
}
